import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CallIcon from "@mui/icons-material/Call";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PermissionGuard } from "../../components/PermissionGuard";
import { QuickBookService } from "./Service";
import { positiveBookingId, quickBookModeLabel, quickBookStatusLabel } from "./display";

const ok = (response) => response?.success === 1 || response?.success === "1";
const FIRST_SUGGESTION_PAGE = { page: 0, pageSize: 20 };

const formatQuickBookTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  }).format(date);
};

const money = (paise, currency = "INR") => {
  if (paise === null || paise === undefined || !Number.isFinite(Number(paise))) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 2,
  }).format(Number(paise) / 100);
};

export const isOperationAllowed = (request) => {
  if (!request || !Number.isSafeInteger(Number(request.version)) || Number(request.version) < 1) return false;
  return request.status === "OPS_ACTION_REQUIRED";
};

export const toAssignmentPayload = (request, suggestion, values, suggestionRequestVersion = request?.version) => ({
  expectedVersion: Number(suggestionRequestVersion),
  providerId: Number(suggestion.providerId),
  providerServiceId: Number(suggestion.providerServiceId),
  expertConfirmedExactSlot: values.expertConfirmedExactSlot === true,
  confirmationNote: String(values.confirmationNote || "").trim(),
});

export const toOperationCancelPayload = (request, values) => ({
  expectedVersion: Number(request.version),
  customerFacingReason: String(values.customerFacingReason || "").trim(),
});

export const operationErrorMessage = (response, action = null) => {
  const code = response?.error?.code;
  const status = response?._http?.status;
  if (code === "QUICK_BOOK_RATE_LIMITED") return "Too many attempts. Wait briefly, then retry the same action.";
  if (code === "QUICK_BOOK_VERSION_CONFLICT") return "This request changed. The latest request and expert suggestions have been loaded.";
  if (code === "QUICK_BOOK_ALREADY_ASSIGNED") return "This request was already assigned. The latest booking link has been loaded.";
  if (code === "QUICK_BOOK_PROVIDER_NOT_ELIGIBLE") return "This expert is no longer eligible for the selected slot. Review the refreshed suggestions.";
  if (code === "QUICK_BOOK_NO_PROVIDER_AVAILABLE") return "No eligible expert is currently available. Review the refreshed suggestions.";
  if (code === "QUICK_BOOK_SLOT_UNAVAILABLE") return "The selected slot is no longer available. The latest request and suggestions have been loaded.";
  if (code === "QUICK_BOOK_DISABLED") return "Quick Book is currently disabled.";
  if (code === "QUICK_BOOK_PERMISSION_DENIED") return "You no longer have permission to manage this Quick Book request. Mutation controls are hidden.";
  if (code === "QUICK_BOOK_REQUEST_NOT_FOUND") return "This Quick Book request no longer exists. The Operations queue has been refreshed.";
  if (action === "call" && status === 409 && code === "QUICK_BOOK_REQUEST_NOT_ACTIONABLE") return "Call outcome is unconfirmed. Verify it in MyOperator before retrying.";
  if (action === "call" && (status === 502 || status === 503)) return "The call outcome may be uncertain. Verify it in MyOperator before retrying.";
  if (code === "QUICK_BOOK_REQUEST_NOT_ACTIONABLE") return "This request can no longer perform that action. Its latest status has been loaded.";
  return response?.message || "The action could not be completed.";
};

const retryNeedsRefresh = (response) => [
  "QUICK_BOOK_VERSION_CONFLICT",
  "QUICK_BOOK_ALREADY_ASSIGNED",
  "QUICK_BOOK_PROVIDER_NOT_ELIGIBLE",
  "QUICK_BOOK_NO_PROVIDER_AVAILABLE",
  "QUICK_BOOK_SLOT_UNAVAILABLE",
  "QUICK_BOOK_REQUEST_NOT_ACTIONABLE",
].includes(response?.error?.code);

export const retryAfterMilliseconds = (response, now = Date.now()) => {
  const raw = response?._http?.retryAfter;
  if (!raw) return 0;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds * 1000);
  const date = new Date(raw).getTime();
  return Number.isFinite(date) ? Math.max(0, date - now) : 0;
};

export const cancellationPolicyText = (disclosure) => {
  const minutes = Number(disclosure?.noFeeBeforeMinutes);
  const percent = Number(disclosure?.lateFeePercent);
  if (!Number.isFinite(minutes) || minutes < 0 || !Number.isFinite(percent) || percent < 0) {
    return "Cancellation policy snapshot is unavailable.";
  }
  return `At least ${minutes / 60} hours before the service: 100% refund/no fee. Below that window: a displayed ${percent}% fee against the final charged total. Expert/CuDel cancellation: no customer fee. Final refund follows the existing Operations process.`;
};

export const createIdempotencyKey = (cryptoApi = window.crypto) => {
  if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID();
  if (typeof cryptoApi?.getRandomValues !== "function") {
    throw new Error("Secure random number generation is unavailable.");
  }
  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const isPlainOperationText = (value) => typeof value === "string"
  && value.trim().length > 0
  && !Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return character === "<" || character === ">" || code < 32 || code === 127;
  });

const PlainItems = ({ title, items }) => (
  <Box>
    <Typography sx={{ fontSize: ".75rem", fontWeight: 600 }}>{title}</Typography>
    {items?.length ? (
      <List dense disablePadding>
        {items.map((item, index) => (
          <ListItem key={`${title}-${index}`} disableGutters sx={{ py: 0 }}>
            <ListItemText primary={`• ${item}`} primaryTypographyProps={{ fontSize: ".72rem" }} />
          </ListItem>
        ))}
      </List>
    ) : <Typography color="text.secondary" sx={{ fontSize: ".7rem" }}>Not supplied</Typography>}
  </Box>
);

const DetailCard = ({ title, children }) => (
  <Card variant="outlined" sx={{ height: "100%", p: 1.5 }}>
    <Typography sx={{ fontSize: ".8rem", fontWeight: 700, mb: 1 }}>{title}</Typography>
    {children}
  </Card>
);

const DetailLine = ({ label, value }) => (
  <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between", py: 0.35 }}>
    <Typography color="text.secondary" sx={{ fontSize: ".7rem" }}>{label}</Typography>
    <Typography sx={{ fontSize: ".7rem", fontWeight: 600, textAlign: "right", overflowWrap: "anywhere" }}>{value ?? "—"}</Typography>
  </Box>
);

const AssignmentDialog = ({ open, request, suggestion, pending, canSubmit, onClose, onSubmit, message }) => {
  const { control, handleSubmit, reset, setError } = useForm({
    defaultValues: { expertConfirmedExactSlot: false, confirmationNote: "" },
  });

  useEffect(() => {
    if (open) reset({ expertConfirmedExactSlot: false, confirmationNote: "" });
  }, [open, reset]);

  const submit = async (values) => {
    const response = await onSubmit(values);
    (response?.error?.fieldErrors || []).forEach(({ field, message: fieldMessage }) => setError(field, { type: "server", message: fieldMessage }));
  };

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="assign-quick-book-title">
      <DialogTitle id="assign-quick-book-title">Assign after phone confirmation</DialogTitle>
      <DialogContent dividers>
        <Box component="form" id="assign-quick-book-form" onSubmit={handleSubmit(submit)} noValidate>
          <Stack spacing={2}>
            {message && <Alert severity="warning">{message}</Alert>}
            <Alert severity="info">Call the expert through the server-resolved action first. Assignment creates the existing accepted booking only after the exact slot is confirmed.</Alert>
            <DetailLine label="Expert" value={suggestion ? `${suggestion.displayName || "Expert"} (#${suggestion.providerId})` : "—"} />
            <DetailLine label="Scheduled start" value={formatQuickBookTime(request?.scheduledStartAt)} />
            <DetailLine label="Scheduled end" value={formatQuickBookTime(request?.scheduledEndAt)} />
            <Controller
              name="expertConfirmedExactSlot"
              control={control}
              rules={{ validate: (value) => value === true || "Confirm that the expert agreed to this exact date and time" }}
              render={({ field, fieldState }) => (
                <Box>
                  <FormControlLabel
                    control={<Checkbox checked={field.value} onChange={(_, checked) => field.onChange(checked)} />}
                    label="The expert confirmed this exact date and time by phone"
                  />
                  {fieldState.error && <Typography color="error" sx={{ fontSize: ".7rem", ml: 4 }}>{fieldState.error.message}</Typography>}
                </Box>
              )}
            />
            <Controller
              name="confirmationNote"
              control={control}
              rules={{
                required: "Confirmation note is required",
                maxLength: { value: 500, message: "Maximum 500 characters" },
                validate: (value) => isPlainOperationText(value) || "Use 1–500 plain-text characters",
              }}
              render={({ field, fieldState }) => (
                <TextField {...field} fullWidth multiline minRows={3} required label="Phone confirmation note" error={Boolean(fieldState.error)} helperText={fieldState.error?.message} inputProps={{ maxLength: 500 }} />
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>Back</Button>
        <Button type="submit" form="assign-quick-book-form" variant="contained" disabled={pending || !canSubmit} startIcon={pending ? <CircularProgress size={16} /> : <AssignmentIndIcon />}>Assign expert</Button>
      </DialogActions>
    </Dialog>
  );
};

const CancellationDialog = ({ open, request, pending, onClose, onSubmit, message }) => {
  const { control, handleSubmit, reset, setError } = useForm({ defaultValues: { customerFacingReason: "" } });

  useEffect(() => {
    if (open) reset({ customerFacingReason: "" });
  }, [open, reset]);

  const submit = async (values) => {
    const response = await onSubmit(values);
    (response?.error?.fieldErrors || []).forEach(({ field, message: fieldMessage }) => setError(field, { type: "server", message: fieldMessage }));
  };

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="cancel-quick-book-title">
      <DialogTitle id="cancel-quick-book-title">Cancel Quick Book request</DialogTitle>
      <DialogContent dividers>
        <Box component="form" id="cancel-quick-book-form" onSubmit={handleSubmit(submit)} noValidate>
          <Stack spacing={2}>
            {message && <Alert severity="warning">{message}</Alert>}
            <Alert severity="warning">
              This cancels only the pre-assignment request. Captured Pay Now payments move to the existing manual refund process; this screen does not initiate a refund.
            </Alert>
            <DetailLine label="Request" value={request?.requestNumber} />
            <DetailLine label="Payment" value={`${quickBookStatusLabel(request?.payment?.state)} · Refund ${quickBookStatusLabel(request?.payment?.refundState)}`} />
            <Controller
              name="customerFacingReason"
              control={control}
              rules={{
                required: "Customer-facing reason is required",
                maxLength: { value: 500, message: "Maximum 500 characters" },
                validate: (value) => isPlainOperationText(value) || "Use 1–500 plain-text characters",
              }}
              render={({ field, fieldState }) => (
                <TextField {...field} fullWidth multiline minRows={3} required label="Customer-facing reason" error={Boolean(fieldState.error)} helperText={fieldState.error?.message} inputProps={{ maxLength: 500 }} />
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>Back</Button>
        <Button type="submit" form="cancel-quick-book-form" variant="contained" color="error" disabled={pending} startIcon={pending ? <CircularProgress size={16} /> : <CancelIcon />}>Cancel request</Button>
      </DialogActions>
    </Dialog>
  );
};

export default function QuickBookRequestDetails({ requestNumber, open, onClose, onChanged }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const keys = useRef(new Map());
  const inFlight = useRef(false);
  const detailLoadId = useRef(0);
  const suggestionLoadId = useRef(0);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionCount, setSuggestionCount] = useState(0);
  const [suggestionPagination, setSuggestionPagination] = useState(FIRST_SUGGESTION_PAGE);
  const [suggestionRequestVersion, setSuggestionRequestVersion] = useState(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionError, setSuggestionError] = useState("");
  const [suggestionsLoadedAt, setSuggestionsLoadedAt] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState("");
  const [assignMessage, setAssignMessage] = useState("");
  const [cancelMessage, setCancelMessage] = useState("");
  const [retryUntil, setRetryUntil] = useState(0);
  const [serverDenied, setServerDenied] = useState(false);
  const rateLimited = retryUntil > Date.now();

  const keyFor = (scope, payload) => {
    const fingerprint = JSON.stringify(payload);
    const current = keys.current.get(scope);
    if (current?.fingerprint === fingerprint) return current.key;
    const key = createIdempotencyKey();
    keys.current.set(scope, { fingerprint, key });
    return key;
  };

  useEffect(() => {
    if (!retryUntil) return undefined;
    const timer = setTimeout(() => setRetryUntil(0), Math.max(0, retryUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [retryUntil]);

  const applyRateLimit = (response) => {
    if (response?.error?.code !== "QUICK_BOOK_RATE_LIMITED") return;
    const delay = response?._http?.retryAfter ? retryAfterMilliseconds(response) : 60000;
    setRetryUntil(Date.now() + delay);
  };

  const handleAuthoritativeActionError = async (response) => {
    if (response?.error?.code === "QUICK_BOOK_PERMISSION_DENIED") {
      setServerDenied(true);
      setAssignOpen(false);
      setCancelOpen(false);
      setNotice({ severity: "error", text: operationErrorMessage(response) });
      return true;
    }
    if (response?.error?.code === "QUICK_BOOK_REQUEST_NOT_FOUND") {
      setAssignOpen(false);
      setCancelOpen(false);
      setSelectedSuggestion(null);
      setRequest(null);
      setSuggestions([]);
      setSuggestionCount(0);
      setSuggestionRequestVersion(null);
      await onChanged?.();
      onClose();
      return true;
    }
    return false;
  };

  const loadSuggestions = useCallback(async (number, pagination) => {
    const loadId = ++suggestionLoadId.current;
    setSuggestionLoading(true);
    setSuggestionError("");
    const response = await QuickBookService.operationSuggestions(number, {
      page: pagination.page + 1,
      limit: pagination.pageSize,
    });
    if (loadId !== suggestionLoadId.current) return response;
    if (ok(response)) {
      setSuggestions(response.data?.items || []);
      setSuggestionCount(response.data?.pagination?.totalItems || 0);
      setSuggestionRequestVersion(Number(response.data?.requestVersion));
      setSuggestionsLoadedAt(new Date());
    } else {
      setSuggestions([]);
      setSuggestionCount(0);
      setSuggestionRequestVersion(null);
      setSuggestionError(operationErrorMessage(response));
    }
    setSuggestionLoading(false);
    return response;
  }, []);

  const refresh = useCallback(async (pagination) => {
    if (!requestNumber) return null;
    const loadId = ++detailLoadId.current;
    setLoading(true);
    setError("");
    const response = await QuickBookService.operationRequest(requestNumber);
    if (loadId !== detailLoadId.current) return response;
    if (ok(response)) {
      const detail = response.data;
      setRequest(detail);
      if (detail?.status === "OPS_ACTION_REQUIRED") await loadSuggestions(requestNumber, pagination);
      else {
        setSuggestions([]);
        setSuggestionCount(0);
        setSuggestionRequestVersion(null);
        setSuggestionError("");
      }
    } else {
      setRequest(null);
      setSuggestions([]);
      setError(operationErrorMessage(response));
      if (response?.error?.code === "QUICK_BOOK_REQUEST_NOT_FOUND") {
        await onChanged?.();
        onClose();
      }
    }
    setLoading(false);
    return response;
  }, [loadSuggestions, onChanged, onClose, requestNumber]);

  useEffect(() => {
    if (!open || !requestNumber) return;
    setNotice(null);
    setRequest(null);
    setSuggestionPagination(FIRST_SUGGESTION_PAGE);
    keys.current.clear();
    refresh(FIRST_SUGGESTION_PAGE);
    return () => {
      detailLoadId.current += 1;
      suggestionLoadId.current += 1;
    };
  }, [open, requestNumber, refresh]);

  useEffect(() => {
    setServerDenied(false);
  }, [open, requestNumber]);

  const refreshAfterMutation = async () => {
    await refresh(suggestionPagination);
    await onChanged?.();
  };

  const callProvider = async (suggestion) => {
    if (inFlight.current || rateLimited) return;
    const scope = `call:${request.requestNumber}:${request.version}:${suggestion.providerId}`;
    const payload = { providerId: suggestion.providerId };
    inFlight.current = true;
    setPendingAction(scope);
    setNotice(null);
    const response = await QuickBookService.callOperationProvider(
      request.requestNumber,
      suggestion.providerId,
      keyFor(scope, payload),
    );
    if (ok(response)) {
      keys.current.delete(scope);
      setNotice({ severity: "success", text: "Expert call accepted. Confirm the exact slot before assigning." });
      await refreshAfterMutation();
    } else {
      const authoritative = await handleAuthoritativeActionError(response);
      applyRateLimit(response);
      if (!authoritative) {
        setNotice({
          severity: response?.error?.retryable ? "warning" : "error",
          text: operationErrorMessage(response, "call"),
          bookingId: positiveBookingId(response?.error?.details?.assignedBooking?.bookingId),
        });
        if (retryNeedsRefresh(response)) await refreshAfterMutation();
      }
    }
    inFlight.current = false;
    setPendingAction("");
  };

  const assign = async (values) => {
    if (inFlight.current || rateLimited) return { success: 0, message: "An action is already in progress." };
    const selectedStillEligible = suggestions.some((suggestion) => suggestion.providerId === selectedSuggestion?.providerId
      && suggestion.providerServiceId === selectedSuggestion?.providerServiceId
      && suggestion.coverageEligible && suggestion.scheduleEligible && suggestion.readinessEligible);
    if (Number(request.version) !== suggestionRequestVersion || !selectedStillEligible) {
      setAssignMessage("The request or selected expert changed. Refresh suggestions before assigning.");
      return { success: 0, message: "Refresh suggestions before assigning." };
    }
    const scope = `assign:${request.requestNumber}:${request.version}:${selectedSuggestion.providerId}:${selectedSuggestion.providerServiceId}`;
    const payload = toAssignmentPayload(request, selectedSuggestion, values, suggestionRequestVersion);
    inFlight.current = true;
    setPendingAction(scope);
    setAssignMessage("");
    const response = await QuickBookService.assignConfirmed(
      request.requestNumber,
      payload,
      keyFor(scope, payload),
    );
    if (ok(response)) {
      keys.current.delete(scope);
      setAssignOpen(false);
      setNotice({ severity: "success", text: "Expert assigned. The accepted booking is ready." });
      await refreshAfterMutation();
    } else {
      const authoritative = await handleAuthoritativeActionError(response);
      applyRateLimit(response);
      if (!authoritative) {
        setAssignMessage(operationErrorMessage(response));
        if (response?.error?.code === "QUICK_BOOK_ALREADY_ASSIGNED") {
          setAssignOpen(false);
          setNotice({ severity: "warning", text: operationErrorMessage(response), bookingId: positiveBookingId(response?.error?.details?.assignedBooking?.bookingId) });
        }
        if (["QUICK_BOOK_PROVIDER_NOT_ELIGIBLE", "QUICK_BOOK_NO_PROVIDER_AVAILABLE"].includes(response?.error?.code)) {
          setAssignOpen(false);
          setSelectedSuggestion(null);
          setNotice({ severity: "warning", text: operationErrorMessage(response) });
        }
        if (retryNeedsRefresh(response)) await refreshAfterMutation();
      }
    }
    inFlight.current = false;
    setPendingAction("");
    return response;
  };

  const cancel = async (values) => {
    if (inFlight.current || rateLimited) return { success: 0, message: "An action is already in progress." };
    const scope = `cancel:${request.requestNumber}:${request.version}`;
    const payload = toOperationCancelPayload(request, values);
    inFlight.current = true;
    setPendingAction(scope);
    setCancelMessage("");
    const response = await QuickBookService.cancelOperationRequest(
      request.requestNumber,
      payload,
      keyFor(scope, payload),
    );
    if (ok(response)) {
      keys.current.delete(scope);
      setCancelOpen(false);
      setNotice({ severity: "success", text: "Request cancelled. Payment and refund status have been refreshed." });
      await refreshAfterMutation();
    } else {
      const authoritative = await handleAuthoritativeActionError(response);
      applyRateLimit(response);
      if (!authoritative) {
        setCancelMessage(operationErrorMessage(response));
        if (response?.error?.code === "QUICK_BOOK_ALREADY_ASSIGNED") {
          setCancelOpen(false);
          setNotice({ severity: "warning", text: operationErrorMessage(response), bookingId: positiveBookingId(response?.error?.details?.assignedBooking?.bookingId) });
        }
        if (retryNeedsRefresh(response)) await refreshAfterMutation();
      }
    }
    inFlight.current = false;
    setPendingAction("");
    return response;
  };

  const suggestionRows = useMemo(() => suggestions.map((suggestion) => ({
    ...suggestion,
    fullyEligible: suggestion.coverageEligible && suggestion.scheduleEligible && suggestion.readinessEligible,
  })), [suggestions]);
  const versionsMatch = Number(request?.version) === suggestionRequestVersion;
  const selectedStillEligible = suggestionRows.some((suggestion) => suggestion.providerId === selectedSuggestion?.providerId
    && suggestion.providerServiceId === selectedSuggestion?.providerServiceId && suggestion.fullyEligible);

  const changeSuggestionPage = (page) => {
    const next = { ...suggestionPagination, page };
    setSuggestionPagination(next);
    loadSuggestions(requestNumber, next);
  };

  const close = () => {
    if (pendingAction) return;
    detailLoadId.current += 1;
    suggestionLoadId.current += 1;
    setAssignOpen(false);
    setCancelOpen(false);
    onClose();
  };

  const noticeBookingId = positiveBookingId(notice?.bookingId);
  const assignedBookingId = positiveBookingId(request?.assignedBooking?.bookingId);

  return (
    <>
      <Dialog open={open} onClose={close} fullScreen={fullScreen} fullWidth maxWidth="xl" aria-labelledby="quick-book-detail-title">
        <DialogTitle id="quick-book-detail-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography component="span" sx={{ fontWeight: 700 }}>Quick Book request</Typography>
            {requestNumber && <Typography component="span" color="primary" sx={{ ml: 1, fontWeight: 700 }}>{requestNumber}</Typography>}
          </Box>
          <Tooltip title="Refresh request">
            <span><IconButton aria-label="Refresh request details" onClick={() => refresh(suggestionPagination)} disabled={loading || Boolean(pendingAction)}><RefreshIcon /></IconButton></span>
          </Tooltip>
          <IconButton aria-label="Close request details" onClick={close} disabled={Boolean(pendingAction)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1, md: 2 } }}>
          {notice && <Alert severity={notice.severity} sx={{ mb: 1 }} action={noticeBookingId ? <Button color="inherit" size="small" href={`/admin/booking-details/${noticeBookingId}`} target="_blank" rel="noreferrer">Open booking</Button> : undefined}>{notice.text}</Alert>}
          {rateLimited && <Alert severity="warning" sx={{ mb: 1 }}>Quick Book actions are temporarily paused until the server retry window ends.</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {loading && !request && <Box sx={{ display: "grid", placeItems: "center", minHeight: 260 }}><CircularProgress aria-label="Loading request details" /></Box>}
          {request && (
            <Stack spacing={1.5}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                <Chip label={quickBookStatusLabel(request.status)} color={request.status === "ASSIGNED" ? "success" : request.status === "FAILED" ? "error" : "warning"} />
                <Typography sx={{ fontSize: ".75rem" }}>Version {request.version}</Typography>
                {request.assignmentSource && <Chip size="small" variant="outlined" label={`Assigned by ${request.assignmentSource}`} />}
                <Box sx={{ flexGrow: 1 }} />
                {!serverDenied && <PermissionGuard feature="booking" permission="canEdit">
                  <Button
                    color="error"
                    variant="outlined"
                    size="small"
                    startIcon={<CancelIcon />}
                    disabled={!isOperationAllowed(request) || Boolean(pendingAction) || rateLimited}
                    onClick={() => { setCancelMessage(""); setCancelOpen(true); }}
                  >
                    Cancel request
                  </Button>
                </PermissionGuard>}
              </Stack>

              {assignedBookingId && (
                <Alert severity="success" action={(
                  <Button color="inherit" size="small" href={`/admin/booking-details/${assignedBookingId}`} target="_blank" rel="noreferrer">Open booking</Button>
                )}>
                  Assigned booking {request.assignedBooking.bookingNumber || `#${assignedBookingId}`} · {quickBookStatusLabel(request.assignedBooking.bookingStatus)}
                </Alert>
              )}

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} lg={2.4}>
                  <DetailCard title="Customer details">
                    <DetailLine label="Customer ID" value={request.customerDetails?.customerId ? `#${request.customerDetails.customerId}` : null} />
                    <DetailLine label="Name" value={request.customerDetails?.name} />
                    <DetailLine label="Mobile" value={request.customerDetails?.mobileNumber} />
                    <DetailLine label="Area" value={[request.customerDetails?.locality, request.customerDetails?.city].filter(Boolean).join(", ") || null} />
                  </DetailCard>
                </Grid>
                <Grid item xs={12} sm={6} lg={2.4}>
                  <DetailCard title="Expert details">
                    {request.expertDetails ? (
                      <>
                        <DetailLine label="Expert ID" value={request.expertDetails.expertId ? `#${request.expertDetails.expertId}` : null} />
                        <DetailLine label="Name" value={request.expertDetails.name} />
                        <DetailLine label="Mobile" value={request.expertDetails.mobileNumber} />
                        <DetailLine label="Assigned by" value={request.expertDetails.assignmentSource} />
                        <DetailLine label="Booking" value={request.expertDetails.bookingNumber} />
                      </>
                    ) : <Typography color="text.secondary" sx={{ fontSize: ".72rem" }}>No expert assigned yet.</Typography>}
                  </DetailCard>
                </Grid>
                <Grid item xs={12} sm={6} lg={2.4}>
                  <DetailCard title="Request snapshot">
                    <DetailLine label="Service" value={request.serviceSnapshot?.title} />
                    <DetailLine label="Mode" value={quickBookModeLabel(request.mode)} />
                    <DetailLine label="Quantity" value={`${request.quantity ?? "—"} ${request.serviceSnapshot?.unitLabel || "unit(s)"}`} />
                    <DetailLine label="Area" value={[request.addressSummary?.locality, request.addressSummary?.city].filter(Boolean).join(", ") || "—"} />
                    <DetailLine label="Scheduled start" value={formatQuickBookTime(request.scheduledStartAt)} />
                    <DetailLine label="Scheduled end" value={formatQuickBookTime(request.scheduledEndAt)} />
                    <DetailLine label="Service time" value={`${request.unitDurationMinutes ?? "—"}m × ${request.quantity} + ${request.bufferMinutes ?? "—"}m buffer`} />
                    <DetailLine label="Created" value={formatQuickBookTime(request.createdAt)} />
                    <DetailLine label="Updated" value={formatQuickBookTime(request.updatedAt)} />
                  </DetailCard>
                </Grid>
                <Grid item xs={12} sm={6} lg={2.4}>
                  <DetailCard title="Price snapshot">
                    <DetailLine label="Unit price" value={money(request.unitAmountPaise, request.currency)} />
                    <DetailLine label="Line total" value={money(request.lineTotalPaise, request.currency)} />
                    <DetailLine label="Platform fee" value={money(request.platformFeePaise, request.currency)} />
                    <DetailLine label="Convenience fee" value={money(request.convenienceFeePaise, request.currency)} />
                    <DetailLine label="Tax" value={money(request.taxPaise, request.currency)} />
                    <Divider sx={{ my: 0.5 }} />
                    <DetailLine label="Final charged total" value={money(request.finalChargedTotalPaise, request.currency)} />
                    {request.finalProviderPayoutTotalPaise !== null && request.finalProviderPayoutTotalPaise !== undefined && (
                      <DetailLine label="Accepted payout snapshot" value={money(request.finalProviderPayoutTotalPaise, request.currency)} />
                    )}
                  </DetailCard>
                </Grid>
                <Grid item xs={12} sm={6} lg={2.4}>
                  <PermissionGuard feature="booking-payment" permission="canView" fallback={(
                    <DetailCard title="Payment"><Alert severity="info">Payment detail is hidden by your permission.</Alert></DetailCard>
                  )}>
                    <DetailCard title="Payment and refund">
                      <DetailLine label="Purpose" value={request.payment?.paymentPurpose} />
                      <DetailLine label="Choice" value={request.payment?.choice || request.paymentChoice} />
                      <DetailLine label="State" value={quickBookStatusLabel(request.payment?.state)} />
                      <DetailLine label="Refund" value={quickBookStatusLabel(request.payment?.refundState)} />
                      <DetailLine label="Order reference" value={request.payment?.safeOrderReference} />
                      <DetailLine label="Payment reference" value={request.payment?.safePaymentReference} />
                      {request.payment?.refundState === "REFUND_REQUIRED" && <Alert severity="warning" sx={{ mt: 1 }}>Manual refund is required through the existing Razorpay Operations process. No refund is initiated here.</Alert>}
                      {request.payment?.refundState === "REFUND_INITIATED" && <Alert severity="info" sx={{ mt: 1 }}>Refund is in progress.</Alert>}
                      {request.payment?.refundState === "REFUND_FAILED" && <Alert severity="error" sx={{ mt: 1 }}>The recorded refund failed. Follow the existing Operations process.</Alert>}
                      {request.payment?.refundState === "REFUNDED" && <Alert severity="success" sx={{ mt: 1 }}>Refund is recorded as completed.</Alert>}
                    </DetailCard>
                  </PermissionGuard>
                </Grid>
              </Grid>

              <Grid container spacing={1.5}>
                <Grid item xs={12} md={7}>
                  <DetailCard title="Service scope">
                    {request.serviceSnapshot?.summary && <Typography sx={{ fontSize: ".72rem", mb: 1 }}>{request.serviceSnapshot.summary}</Typography>}
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}><PlainItems title="Included" items={request.serviceSnapshot?.scope?.includedItems} /></Grid>
                      <Grid item xs={12} sm={4}><PlainItems title="Not included" items={request.serviceSnapshot?.scope?.excludedItems} /></Grid>
                      <Grid item xs={12} sm={4}><PlainItems title="Preparation" items={request.serviceSnapshot?.scope?.preparationItems} /></Grid>
                    </Grid>
                    {request.serviceSnapshot?.scope?.inspectionDisclaimer && <Alert severity="info" sx={{ mt: 1 }}>{request.serviceSnapshot.scope.inspectionDisclaimer}</Alert>}
                  </DetailCard>
                </Grid>
                <Grid item xs={12} md={5}>
                  <DetailCard title="Cancellation snapshot">
                    <Typography sx={{ fontSize: ".72rem", mb: 1 }}>
                      {cancellationPolicyText(request.serviceSnapshot?.cancellationDisclosure)}
                    </Typography>
                    <DetailLine label="Cancelled at" value={formatQuickBookTime(request.cancelledAt)} />
                    <DetailLine label="Cancelled by" value={request.cancelledByType} />
                    <DetailLine label="Reason" value={request.cancellationReason} />
                    {request.cancellationSnapshot && (
                      <>
                        <DetailLine label="Displayed fee" value={money(request.cancellationSnapshot.displayedFeePaise, request.currency)} />
                        <DetailLine label="Displayed refund" value={money(request.cancellationSnapshot.displayedRefundPaise, request.currency)} />
                      </>
                    )}
                  </DetailCard>
                </Grid>
              </Grid>

              <DetailCard title="Assignment attempts">
                {request.attempts?.length ? (
                  <Box sx={{ overflowX: "auto" }}>
                    <Box component="table" sx={{ borderCollapse: "collapse", minWidth: 920, width: "100%", "th, td": { borderBottom: "1px solid", borderColor: "divider", p: 1, textAlign: "left", fontSize: ".7rem" } }}>
                      <thead><tr><th>Attempt</th><th>Expert</th><th>Source / status</th><th>Offer / response</th><th>Reason</th><th>Configured payout snapshot</th><th>Actor</th></tr></thead>
                      <tbody>
                        {request.attempts.map((attempt) => (
                          <tr key={attempt.attemptId}>
                            <td>{attempt.attemptId}</td>
                            <td>Expert #{attempt.providerId}<br />Service #{attempt.providerServiceId}</td>
                            <td>{attempt.source} · {quickBookStatusLabel(attempt.status)}</td>
                            <td>{formatQuickBookTime(attempt.offeredAt)}<br />{formatQuickBookTime(attempt.respondedAt)}</td>
                            <td>{attempt.declineReasonCode || attempt.failureReason || "—"}{attempt.declineNote ? <><br />{attempt.declineNote}</> : null}</td>
                            <td>{money(attempt.providerUnitPayoutPaise, request.currency)} × {request.quantity}<br />{money(attempt.providerPayoutTotalPaise, request.currency)}</td>
                            <td>{attempt.actorType || "—"}{attempt.actorId ? ` #${attempt.actorId}` : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Box>
                  </Box>
                ) : <Typography color="text.secondary" sx={{ fontSize: ".72rem" }}>No assignment attempts recorded.</Typography>}
              </DetailCard>

              {request.status === "OPS_ACTION_REQUIRED" && (
                <DetailCard title="Live expert suggestions">
                  <Alert id="quick-book-calling-disabled" severity="info" sx={{ mb: 1 }}>
                    MyOperator calling is temporarily unavailable for Quick Book. Contact the expert through an approved Operations channel, confirm the exact slot, then use Assign confirmed.
                  </Alert>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
                    <Typography color="text.secondary" sx={{ fontSize: ".7rem", flexGrow: 1 }}>
                      Revalidated by the backend{suggestionsLoadedAt ? ` · refreshed ${formatQuickBookTime(suggestionsLoadedAt)}` : ""}. Phone numbers are never returned to this page.
                    </Typography>
                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadSuggestions(requestNumber, suggestionPagination)} disabled={suggestionLoading || Boolean(pendingAction)}>Refresh suggestions</Button>
                  </Stack>
                  {suggestionError && <Alert severity="warning" sx={{ mb: 1 }}>{suggestionError}</Alert>}
                  {suggestionLoading && !suggestions.length && <Box sx={{ display: "grid", placeItems: "center", py: 3 }}><CircularProgress aria-label="Loading expert suggestions" size={28} /></Box>}
                  {!suggestionLoading && !suggestionError && !suggestionRows.length && <Alert severity="info">No eligible experts are currently available. Refresh later or cancel the request with a customer-facing reason.</Alert>}
                  <Grid container spacing={1}>
                    {suggestionRows.map((suggestion) => {
                      const callScope = `call:${request.requestNumber}:${request.version}:${suggestion.providerId}`;
                      return (
                        <Grid item xs={12} lg={6} key={`${suggestion.providerId}-${suggestion.providerServiceId}`}>
                          <Card variant="outlined" sx={{ p: 1.25 }}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography sx={{ fontSize: ".78rem", fontWeight: 700 }}>{suggestion.displayName || "Expert"} <Box component="span" color="primary.main">#{suggestion.providerId}</Box></Typography>
                                <Typography color="text.secondary" sx={{ fontSize: ".68rem" }}>Priority {suggestion.priority} · {suggestion.distanceKm ?? "—"} km · Last assigned {formatQuickBookTime(suggestion.lastAssignedAt)}</Typography>
                                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ my: 0.75 }}>
                                  <Chip size="small" color={suggestion.coverageEligible ? "success" : "error"} label={`Coverage: ${suggestion.coverageEligible ? "Yes" : "No"}`} />
                                  <Chip size="small" color={suggestion.scheduleEligible ? "success" : "error"} label={`Schedule: ${suggestion.scheduleEligible ? "Yes" : "No"}`} />
                                  <Chip size="small" color={suggestion.readinessEligible ? "success" : "error"} label={`Ready: ${suggestion.readinessEligible ? "Yes" : "No"}`} />
                                </Stack>
                                <Typography sx={{ fontSize: ".7rem" }}>Configured payout for this request: {money(suggestion.providerPayoutTotalPaise, request.currency)} ({money(suggestion.providerUnitPayoutPaise, request.currency)} per unit)</Typography>
                              </Box>
                              {!serverDenied && <PermissionGuard feature="booking" permission="canEdit">
                                <Stack spacing={0.75}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={pendingAction === callScope ? <CircularProgress size={14} /> : <CallIcon />}
                                    disabled
                                    aria-describedby="quick-book-calling-disabled"
                                    onClick={() => callProvider(suggestion)}
                                  >Call expert</Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AssignmentIndIcon />}
                                    disabled={!suggestion.fullyEligible || !isOperationAllowed(request) || !versionsMatch || Boolean(pendingAction) || rateLimited}
                                    onClick={() => { setSelectedSuggestion(suggestion); setAssignMessage(""); setAssignOpen(true); }}
                                  >Assign confirmed</Button>
                                </Stack>
                              </PermissionGuard>}
                            </Stack>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                  {suggestionCount > suggestionPagination.pageSize && (
                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                      <Button size="small" disabled={suggestionPagination.page === 0 || suggestionLoading} onClick={() => changeSuggestionPage(suggestionPagination.page - 1)}>Previous</Button>
                      <Typography sx={{ fontSize: ".7rem", alignSelf: "center" }}>Page {suggestionPagination.page + 1}</Typography>
                      <Button size="small" disabled={(suggestionPagination.page + 1) * suggestionPagination.pageSize >= suggestionCount || suggestionLoading} onClick={() => changeSuggestionPage(suggestionPagination.page + 1)}>Next</Button>
                    </Stack>
                  )}
                </DetailCard>
              )}

              <DetailCard title="Audit timeline">
                {request.audit?.length ? request.audit.map((entry, index) => (
                  <Box key={`${entry.event}-${entry.occurredAt}-${index}`} sx={{ display: "flex", gap: 1, py: 0.5, borderBottom: index < request.audit.length - 1 ? "1px solid" : 0, borderColor: "divider" }}>
                    <Typography sx={{ minWidth: 160, fontSize: ".68rem" }}>{formatQuickBookTime(entry.occurredAt)}</Typography>
                    <Typography sx={{ flexGrow: 1, fontSize: ".7rem", fontWeight: 600 }}>{entry.event || `${entry.previousStatus || "—"} → ${entry.newStatus || "—"}`}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: ".68rem" }}>{entry.actorType || "SYSTEM"}{entry.actorId ? ` #${entry.actorId}` : ""}</Typography>
                  </Box>
                )) : <Typography color="text.secondary" sx={{ fontSize: ".72rem" }}>No audit events recorded.</Typography>}
              </DetailCard>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <AssignmentDialog
        open={assignOpen}
        request={request}
        suggestion={selectedSuggestion}
        pending={pendingAction.startsWith("assign:")}
        canSubmit={!serverDenied && isOperationAllowed(request) && versionsMatch && selectedStillEligible && !rateLimited}
        message={assignMessage}
        onClose={() => setAssignOpen(false)}
        onSubmit={assign}
      />
      <CancellationDialog
        open={cancelOpen}
        request={request}
        pending={pendingAction.startsWith("cancel:")}
        message={cancelMessage}
        onClose={() => setCancelOpen(false)}
        onSubmit={cancel}
      />
    </>
  );
}
